"use client";
import {
  IconButton,
  IconButtonIcon
} from "../chunk-EG46MEUM.js";
import "../chunk-SRTWNKEZ.js";
import {
  cx
} from "../chunk-RLQEQISX.js";

// src/components/toast.tsx
import { Cross1Icon } from "@radix-ui/react-icons";
import { Slot } from "@radix-ui/react-slot";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext
} from "react";
import {
  Button as ButtonPrimitive,
  Heading as HeadingPrimitive,
  Text as TextPrimitive,
  useLocale
} from "react-aria-components";
import { toast as sonner, Toaster as ToasterPrimitive } from "sonner";
import { jsx } from "react/jsx-runtime";
var uniqueId = 0;
var ToastStateContext = createContext({ id: 0 });
var closeToast = (id) => sonner.dismiss(id);
var useCloseToast = () => {
  const { id } = useContext(ToastStateContext);
  return useCallback(() => closeToast(id), [id]);
};
var useRenderToast = () => {
  const { direction } = useLocale();
  return (element, options = {}) => {
    const {
      /** The amount of time in milliseconds that the toast automatically remains open. */
      duration,
      /** The unique identifier of the toast. */
      id: defaultId,
      /** Whether to mark the toast as important to screen readers. */
      isImportant = false,
      /** The event handler called when the toast automatically closes. */
      onAutoClose,
      /** The event handler called when the toast is intentionally dismissed. */
      onDismiss,
      /** The position of the toast. */
      position: smartPosition = "bottom-end"
    } = options;
    let position;
    switch (smartPosition) {
      case "bottom-end": {
        position = direction === "ltr" ? "bottom-right" : "bottom-left";
        break;
      }
      case "bottom-start": {
        position = direction === "ltr" ? "bottom-left" : "bottom-right";
        break;
      }
      case "top-end": {
        position = direction === "ltr" ? "top-right" : "top-left";
        break;
      }
      case "top-start": {
        position = direction === "ltr" ? "top-left" : "top-right";
        break;
      }
      default: {
        position = smartPosition;
        break;
      }
    }
    let id = defaultId;
    if (id == null) {
      uniqueId += 1;
      id = uniqueId;
    }
    return sonner.custom(
      () => {
        const state = { id };
        return /* @__PURE__ */ jsx(ToastStateContext.Provider, { value: state, children: element });
      },
      {
        dismissible: true,
        ...duration === void 0 ? {} : { duration },
        id,
        important: isImportant,
        ...onAutoClose === void 0 ? {} : { onAutoClose },
        ...onDismiss === void 0 ? {} : { onDismiss },
        position,
        unstyled: true
      }
    );
  };
};
var Toaster = (properties) => {
  const { className, gap } = properties;
  return /* @__PURE__ */ jsx(
    ToasterPrimitive,
    {
      ...className === void 0 ? {} : { className },
      ...gap === void 0 ? {} : { gap }
    }
  );
};
var Toast = forwardRef(({ asChild = false, className, ...properties }, reference) => {
  const Component = asChild ? Slot : "div";
  const mergedClassName = cx(
    "dark:bg-neutral-dark-2 outline-neutral-light-a-3 dark:outline-neutral-dark-a-6 flex w-[var(--width)] overflow-hidden rounded-lg bg-white p-4 font-sans shadow-lg outline outline-1 -outline-offset-1",
    className
  );
  return /* @__PURE__ */ jsx(Component, { className: mergedClassName, ref: reference, ...properties });
});
Toast.displayName = "Toast";
var ToastTitle = forwardRef(({ className, ...properties }, reference) => {
  const mergedClassName = cx("text-neutral-12 text-sm font-medium", className);
  return /* @__PURE__ */ jsx(HeadingPrimitive, { className: mergedClassName, slot: "title", ...properties, ref: reference });
});
ToastTitle.displayName = "ToastTitle";
var ToastDescription = forwardRef(({ className, ...properties }, reference) => {
  const mergedClassName = cx("text-neutral-11 mt-1 block text-sm", className);
  return /* @__PURE__ */ jsx(TextPrimitive, { className: mergedClassName, slot: "description", ...properties, ref: reference });
});
ToastDescription.displayName = "ToastDescription";
var ToastIcon = forwardRef(({ asChild = false, className, ...properties }, reference) => {
  const Component = asChild ? Slot : "svg";
  const mergedClassName = cx("text-neutral-11 me-2.5 size-5 shrink-0", className);
  return /* @__PURE__ */ jsx(Component, { "aria-hidden": true, className: mergedClassName, ref: reference, ...properties });
});
ToastIcon.displayName = "ToastIcon";
var ToastCloseButtonPrimitive = forwardRef(({ asChild = false, ...properties }, reference) => {
  const close = useCloseToast();
  const Component = asChild ? Slot : ButtonPrimitive;
  return /* @__PURE__ */ jsx(Component, { onPress: close, ...properties, ref: reference });
});
ToastCloseButtonPrimitive.displayName = "ToastCloseButtonPrimitive";
var ToastCloseIconButton = forwardRef(({ className, variant = "ghost", ...properties }, reference) => {
  const mergedClassName = cx("size-7", className);
  const close = useCloseToast();
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      className: mergedClassName,
      onPress: close,
      variant,
      ...properties,
      ref: reference
    }
  );
});
ToastCloseIconButton.displayName = "ToastCloseIconButton";
var ToastCloseIconButtonIcon = forwardRef((properties, reference) => /* @__PURE__ */ jsx(IconButtonIcon, { "aria-hidden": true, asChild: true, ref: reference, ...properties, children: /* @__PURE__ */ jsx(Cross1Icon, {}) }));
ToastCloseIconButtonIcon.displayName = "ToastCloseIconButtonIcon";
export {
  Toast,
  ToastCloseButtonPrimitive,
  ToastCloseIconButton,
  ToastCloseIconButtonIcon,
  ToastDescription,
  ToastIcon,
  ToastStateContext,
  ToastTitle,
  Toaster,
  closeToast,
  useCloseToast,
  useRenderToast
};
